import React, { useCallback, useEffect, useState } from "react";
import styles from "./QMonthDetails.module.scss";
import ApprovalQMonthSection from "../QMonthDetails/ApprovalQMonthSection/ApprovalQMonthSection";
import HATVNotes from "../../HATVNotes/HATVNotes";
import HATVDocument from "../../HATVDocument/HATVDocument";
import AIInsight from "../../../common/InsightAI/InsightAI";
import HATVLeaderDetails from "../../HATVLeaderDetails/HATVLeaderDetails";
import HATVLegs from "../../HATVLegs/HATVLegs";
import { downloadDocument, useProfileParams } from "../../../../utils/common";
import ErrorHandlingComponent from "../../../../components/ErrorHandling/ErrorHandling";
import HATVBCRInfo from "../../HATVBCRInfo/HATVBCRInfo";
import SuccessionDocLoader from "../../../common/Loaders/SuccessionDocLoader/SuccessionDocLoader";
import { useTranslation } from "react-i18next";
import ABOBackgroundLoader from "../../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader";
import { useAppSelector } from "../../../../redux/hooks";
import Icon from "../../../common/Icon/Icon";
import HATVGarUplineImpact from "../HATVGarUplineImpact/HATVGarUplineImpact";
import HATVMarketUplineImpact from "../HATVMarketUplineImpact/HATVMarketUplineImpact";
import {
  useLazyGetHATVQMonthUplineImpactQuery,
  useLazyGetHATVQMonthUplineMarketImpactQuery,
} from "../../../../redux/services/hatvQmonthUplineImpactAPI";
import {
  useGetHATVQmonthDetailsAIInsightSummaryQuery,
  useLazyGetHATVQmonthDetailsAPIQuery,
} from "../../../../redux/services/hatvQmonthDetailsAPI";
import CalendarDropdown from "../../HATVDashboard/CalendarDropdown/CalendarDropdown";
import { useMonthOptions } from "../../../Context/MonthYearOptionsContext";
import ToastMessage from "../../../common/ToastMessage/ToastMessage";
import { toastMessageType } from "../../../../types/types";
import { getHatvNotesQueryHook } from "../../../../utils/common";
import { useKPIList } from "../../../Context/KPIListContext";
import { getYearFromAward } from "../../../../utils/common";
import { useLocation } from "react-router-dom";

interface QMonthDetailsType {
  onMonthYearChange: (month: string) => void;
  bcrInfo: BCRInfo;
}
// Disable feature for all market as of now AMW-2404
const shouldShowInsightSummaryInQmonth = false;

const QMonthDetails: React.FC<QMonthDetailsType> = ({
  onMonthYearChange,
  bcrInfo,
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const { refetchKPIList } = useKPIList();

  const qMonthNotesAPI = getHatvNotesQueryHook("qMonth");
  const [isAPIError, setIsAPIError] = useState(false);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [isAPIDataLoading, setAPIDataLoading] = useState(false);
  const [UplineImpactdata, setUplineImpactData] = useState<any>(null);
  const [uplineMarketData, setUplineMarketData] = useState<any>(null);
  const [hatvQMonthDetailsData, setHatvQMonthDetailsData] = useState<any>(null);
  const {
    monthYearList,
    selectedYearMonth,
    setSelectedYearMonth,
    selectedPYYear,
    selectedPYYearMonth,
    isLoading: isMonthYearLoading,
    isError: isMonthYearError,
    refetchOptions,
  } = useMonthOptions();

  const [isStatusUpdate, setIsStatusUpdate] = useState<string | null>(null);
  const isInitialLoading = isMonthYearLoading || !selectedYearMonth;
  const performanceYear = Number(selectedYearMonth);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const qualifiedMonth = searchParams.get("qualifiedmonth");

  useEffect(() => {
    qualifiedMonth && setSelectedYearMonth(qualifiedMonth);
  }, [qualifiedMonth]);

  const [toastConfig, setToastConfig] = useState({
    message: "",
    type: "" as toastMessageType,
    show: false,
  });

  const setShowToast = (show: boolean) =>
    setToastConfig((prev) => ({ ...prev, show }));

  useEffect(() => {
    if (!selectedYearMonth && monthYearList.length > 0) {
      setSelectedYearMonth(monthYearList[0]);
    }
  }, [monthYearList, selectedYearMonth]);

  useEffect(() => {
    const newYearMonth = getYearFromAward(selectedPYYear ?? ""); //202509
    if (selectedYearMonth !== newYearMonth) {
      setSelectedYearMonth(newYearMonth);
    }
  }, [selectedPYYear]);

  const handleAPIError = () => {
    setIsAPIError(true);
  };

  // TODO: use below from API once BCR API integrated

  const closingMonth = performanceYear;
  const [
    getHATVQmonthUplineImpacteData,
    {
      isLoading: isUplineImpactLoading,
      isFetching: isUplineImpactFetching,
      isError: isUplineImpactAPIError,
    },
  ] = useLazyGetHATVQMonthUplineImpactQuery();

  const [
    getHATVQmonthUplineMarketImpacteData,
    {
      isLoading: isUplineMarketLoading,
      isError: isUplineImpactMarketAPIError,
      isFetching: isUplineImpactMarketFetching,
    },
  ] = useLazyGetHATVQMonthUplineMarketImpactQuery();

  const [
    getHatvQMonthDetailsData,
    {
      isLoading: isQmonthDetailsLoading,
      isError: isQmonthDetailsAPIError,
      isFetching: isQmonthDetailsFetching,
    },
  ] = useLazyGetHATVQmonthDetailsAPIQuery();

  useEffect(() => {
    if (typeof selectedYearMonth === "string" && selectedYearMonth.length > 0) {
      getHATVQmonthUplineImpacteData({
        closingMonth: selectedYearMonth,
        affiliateCode,
        aboNumber,
      })
        .then((resp) => {
          setUplineImpactData(resp?.data?.body?.uplineImpactAbo);
        })
        .catch((err) => {
          setUplineImpactData(null);
        });
    }
  }, [affiliateCode, aboNumber, selectedYearMonth]);

  useEffect(() => {
    if (typeof selectedYearMonth === "string" && selectedYearMonth.length > 0) {
      getHatvQMonthDetailsData({
        closingMonth: selectedYearMonth,
        affiliateCode,
        aboNumber,
      })
        .then((resp) => {
          setHatvQMonthDetailsData(resp?.data?.body?.qDetail);
        })
        .catch((err) => {
          setHatvQMonthDetailsData(null);
        });
    }
    setIsStatusUpdate("");
  }, [affiliateCode, aboNumber, selectedYearMonth, isStatusUpdate]);

  const {
    data: hatvQMonthSummaryInsightData,
    isLoading: isQmonthAIInsightLoading,
    isFetching: isQmonthAIInsightFetching,
  } = useGetHATVQmonthDetailsAIInsightSummaryQuery(
    {
      closingMonth,
      affiliateCode,
      aboNumber,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !shouldShowInsightSummaryInQmonth,
    }
  );

  useEffect(() => {
    if (typeof selectedYearMonth === "string" && selectedYearMonth.length > 0) {
      getHATVQmonthUplineMarketImpacteData({
        closingMonth: selectedYearMonth,
        affiliateCode,
        aboNumber,
      })
        .then((resp) => {
          setUplineMarketData(resp?.data?.body?.uplineImpactAbo);
        })
        .catch((err) => {
          setUplineMarketData(null);
        });
    }
  }, [affiliateCode, aboNumber, selectedYearMonth]);

  const handleSelectedCode = useCallback(
    (value: string) => {
      setSelectedYearMonth(value);
    },
    [selectedYearMonth]
  );

  useEffect(() => {
    if (selectedYearMonth) {
      onMonthYearChange(selectedYearMonth);
    }
  }, [selectedYearMonth]);

  const handleAPIStatus = (status: string) => {
    if (status === "success") {
      setToastConfig({
        show: true,
        message: `Successfully updated award status`,
        type: "success",
      });
      refetchKPIList();
    } else if (status === "fail") {
      setToastConfig({
        show: true,
        message: `Failed to update award status. Please try again!`,
        type: "error",
      });
    }
    setIsStatusUpdate && setIsStatusUpdate(status);
  };

  return (
    <div
      className={`award_details_container ${styles.Q_Month_details_container}`}
    >
      <div className={styles.year_playbook_wrapper}>
        {isInitialLoading ? (
          <>
            <div
              className={`details_loader animate ${styles.calender_loader}`}
            />
          </>
        ) : (
          <div className={styles.time_selector}>
            <CalendarDropdown
              monthYearList={monthYearList}
              onselectionMonthYear={handleSelectedCode}
              selectedMonth={selectedYearMonth}
              isOnlyYear={false}
              isLoading={isInitialLoading}
              isError={isMonthYearError}
            />
          </div>
        )}

        <div className={`separator_line ${styles.separator_line}`} />

        <div
          onClick={() => downloadDocument("playbook-hatv")}
          className={`playbook_link ${styles.playbook_link}`}
        >
          <Icon
            name={"playbook_icon"}
            size={14}
            className={`playbook_icon ${styles.playbook_icon}`}
            color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
          />
          <span className={`playbook_text ${styles.playbook_text}`}>
            {t("viewPlaybook")}
          </span>
        </div>
      </div>
      <div className={styles.section_wrapper}>
        {isQmonthDetailsLoading || isQmonthDetailsFetching ? (
          <ABOBackgroundLoader num={1} heading={""} />
        ) : isQmonthDetailsAPIError ? (
          <ErrorHandlingComponent error={"genericError"} />
        ) : (
          <ApprovalQMonthSection
            selectedMonth={performanceYear}
            qmonthDetailsData={hatvQMonthDetailsData || []}
            setIsStatusUpdate={handleAPIStatus}
          />
        )}
      </div>
      {/* TODO: hardcoded data added for now replace it with API response */}
      {shouldShowInsightSummaryInQmonth &&
        (isQmonthAIInsightLoading || isQmonthAIInsightFetching ? (
          <div className={`${styles.ai_insight_wrapper} animate`}></div>
        ) : (
          <div className={styles.ai_summary}>
            <AIInsight
              content={hatvQMonthSummaryInsightData?.body?.summary}
              showDropdown={true}
              isDropdownEnabled={true}
              flowerIconSize={24}
              infoIconSize={12}
              title={"ai_summary"}
              titleSize={16}
            />
          </div>
        ))}
      <div className={styles.section_wrapper}>
        <HATVNotes
          type={"qapproval"}
          isStatusUpdate={isStatusUpdate}
          hatvNotesAPI={qMonthNotesAPI}
        />
      </div>
      <div className={styles.section_wrapper}>
        <HATVDocument type={"qapproval"} />
      </div>
      <div className={styles.section_wrapper}>
        <HATVLeaderDetails selectedYearMonth={selectedYearMonth ?? ""} />
      </div>
      <div className={styles.section_wrapper} data-testid="qmonth_Legs">
        <HATVLegs selectedYearMonth={selectedYearMonth ?? ""} />
      </div>
      <div className={styles.section_wrapper}>
        {isUplineImpactMarketFetching || isUplineMarketLoading ? (
          <SuccessionDocLoader num={1} heading={t("uplineImpact_heading")} />
        ) : (
          <>
            <div className={styles.title} data-testid="Upline_impact_testId">
              {t("uplineImpact_heading")}
            </div>
            <HATVMarketUplineImpact
              tableData={isUplineImpactMarketAPIError ? null : uplineMarketData}
              isLoading={false}
            />
          </>
        )}
      </div>
      <div className={styles.section_wrapper}>
        {isUplineImpactFetching || isUplineImpactLoading ? (
          <SuccessionDocLoader num={1} heading={t("garUplineImpact_heading")} />
        ) : (
          <>
            <div className={styles.title} data-testid="BCR_Info_testId">
              {t("garUplineImpact_heading")}
            </div>
            <HATVGarUplineImpact
              tableData={isUplineImpactAPIError ? null : UplineImpactdata}
              isLoading={false}
            />
          </>
        )}
      </div>
      <div className={styles.section_wrapper}>
        {bcrInfo.isBCRFetching || bcrInfo.isBCRLoading ? (
          <SuccessionDocLoader num={1} heading={t("BCR_heading")} />
        ) : (
          <>
            <div className={styles.title} data-testid="BCR_Info_testId">
              {t("BCR_heading")}
            </div>
            <HATVBCRInfo
              tableData={bcrInfo.isBCRAPIError ? [] : bcrInfo?.bcrData?.body}
              isLoading={false}
            />
          </>
        )}
      </div>
      <ToastMessage
        message={toastConfig.message}
        toastType={toastConfig.type}
        showToast={toastConfig.show}
        setShowToast={setShowToast}
        customStyles={{
          top: "64px",
        }}
        autoDismissal
      />
    </div>
  );
};

export default QMonthDetails;
